import { 
  AuthResponse, 
  LoginRequest, 
  RegisterRequest, 
  UserInfo, 
  UpdateProfileRequest,
  SendVerificationEmailRequest,
  VerifyEmailRequest,
  EmailVerificationResponse,
  EmailVerificationStatus,
  InitiateAccountDeletionRequest,
  AccountDeletionResponse,
  CancelDeletionResponse,
  AccountDeletionStatusResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  PasswordResetResponse,
  AppleSignInRequest
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Callback function to clear auth state on 401 errors
let onUnauthorized: (() => void) | null = null;

export const setUnauthorizedCallback = (callback: () => void) => {
  onUnauthorized = callback;
};

class AuthApiService {
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Get token from localStorage for authenticated requests
    const token = localStorage.getItem('auth-store');
    let authToken = null;
    
    if (token) {
      try {
        const parsed = JSON.parse(token);
        authToken = parsed.state?.token;
      } catch (error) {
        console.error('Failed to parse auth token:', error);
      }
    }

    if (authToken) {
      defaultHeaders['Authorization'] = `Bearer ${authToken}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        // Handle 401 Unauthorized specifically
        if (response.status === 401) {
          // Only call the unauthorized callback if we had a token
          // This prevents infinite loops on login/register endpoints
          if (authToken && onUnauthorized && !endpoint.includes('/auth/login') && !endpoint.includes('/auth/register')) {
            console.warn('Token expired or invalid, clearing auth state');
            onUnauthorized();
          }
          throw new Error('Authentication required');
        }

        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.Message || errorMessage;
        } catch {
          // If response is not JSON, use the default error message
        }
        
        throw new Error(errorMessage);
      }

      // Handle empty responses (like logout)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        return {} as T;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error occurred');
    }
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    return this.makeRequest<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async login(credentials: LoginRequest): Promise<AuthResponse> {
    return this.makeRequest<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async logout(): Promise<void> {
    return this.makeRequest<void>('/auth/logout', {
      method: 'POST',
    });
  }

  async getCurrentUser(): Promise<UserInfo> {
    return this.makeRequest<UserInfo>('/auth/me', {
      method: 'GET',
    });
  }

  async updateProfile(profileData: UpdateProfileRequest): Promise<UserInfo> {
    return this.makeRequest<UserInfo>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    return this.makeRequest<AuthResponse>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });
  }

  async googleSignIn(googleData: {
    email: string;
    firstName: string;
    lastName: string;
    googleId: string;
    avatar?: string;
  }): Promise<AuthResponse> {
    return this.makeRequest<AuthResponse>('/auth/google-signin', {
      method: 'POST',
      body: JSON.stringify(googleData),
    });
  }

  async appleSignIn(appleData: AppleSignInRequest): Promise<AuthResponse> {
    return this.makeRequest<AuthResponse>('/auth/apple-signin', {
      method: 'POST',
      body: JSON.stringify(appleData),
    });
  }

  // Email verification methods
  async sendVerificationEmail(data: SendVerificationEmailRequest): Promise<EmailVerificationResponse> {
    return this.makeRequest<EmailVerificationResponse>('/auth/send-verification-email', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async verifyEmail(data: VerifyEmailRequest): Promise<EmailVerificationResponse> {
    return this.makeRequest<EmailVerificationResponse>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async resendVerificationEmail(data: SendVerificationEmailRequest): Promise<EmailVerificationResponse> {
    return this.makeRequest<EmailVerificationResponse>('/auth/resend-verification-email', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async checkEmailVerification(email: string): Promise<EmailVerificationStatus> {
    return this.makeRequest<EmailVerificationStatus>(`/auth/check-email-verification?email=${encodeURIComponent(email)}`, {
      method: 'GET',
    });
  }

  // Account Deletion methods
  async deleteAccount(data: InitiateAccountDeletionRequest): Promise<AccountDeletionResponse> {
    return this.makeRequest<AccountDeletionResponse>('/auth/delete-account', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async cancelAccountDeletion(): Promise<CancelDeletionResponse> {
    return this.makeRequest<CancelDeletionResponse>('/auth/cancel-deletion', {
      method: 'POST',
    });
  }

  async getAccountDeletionStatus(): Promise<AccountDeletionStatusResponse> {
    return this.makeRequest<AccountDeletionStatusResponse>('/auth/deletion-status', {
      method: 'GET',
    });
  }

  // Password Reset methods
  async forgotPassword(data: ForgotPasswordRequest): Promise<PasswordResetResponse> {
    return this.makeRequest<PasswordResetResponse>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async resetPassword(data: ResetPasswordRequest): Promise<PasswordResetResponse> {
    return this.makeRequest<PasswordResetResponse>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async resendPasswordResetOtp(data: ForgotPasswordRequest): Promise<PasswordResetResponse> {
    return this.makeRequest<PasswordResetResponse>('/auth/resend-password-reset-otp', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export const authApi = new AuthApiService(); 